import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { useShipmentFileFeed, useOpenToolbarFile } from '#/entities/shipment'
import type { ShipmentFileFeedItem } from '#/entities/shipment'
import { formatBytes } from '#/shared/lib/format'
import { HistoryRow, HistoryViewLink } from '#/shared/ui/history-row'

import { GroupedItems } from './grouped-items'

function fileMeta(file: ShipmentFileFeedItem): ReactNode[] {
  const meta: ReactNode[] = []
  if (file.timestamp) meta.push(file.timestamp)
  if (file.authorName) meta.push(file.authorName)
  const size = formatBytes(file.sizeBytes)
  if (size) meta.push(size)
  return meta
}

function FileListRow({
  file,
  onOpen,
}: {
  file: ShipmentFileFeedItem
  onOpen: (fileId: string) => void
}) {
  const { t } = useTranslation()
  return (
    <HistoryRow
      title={file.name}
      meta={fileMeta(file)}
      action={
        <HistoryViewLink onClick={() => onOpen(file.id)}>
          {t('orderDetails.history.actions.view')}
        </HistoryViewLink>
      }
    />
  )
}

export function ShipmentFiles({
  shipmentId,
}: {
  shipmentId: string | undefined
}) {
  const { t } = useTranslation()
  const openFile = useOpenToolbarFile()
  const { files, isLoading } = useShipmentFileFeed(shipmentId ?? '', {
    enabled: Boolean(shipmentId),
  })

  if (isLoading || files.length === 0) return null

  return (
    <GroupedItems
      className="px-4 pb-4"
      title={t('detailSidebar.files.title')}
      toggleLabel={t('detailSidebar.files.toggleGroup')}
      items={files}
      getKey={(file) => file.id}
      renderItem={(file) => <FileListRow file={file} onOpen={openFile} />}
      moreLabel={(count) => t('detailSidebar.files.moreFiles', { count })}
      showMoreLabel={(count) => t('detailSidebar.files.showMore', { count })}
    />
  )
}
